/**
 * src/utils/customsCalc.js
 *
 * The one customs tax function in the app. Every caller — declaration,
 * invoice display, Recepción Digital, agency and viajero screens — taxes
 * through calcPackageTax(). There is no other copy of the formula.
 *
 * No React. No DB writes. No default rates: every rate comes from a
 * customs_config row (fetchCustomsConfigRow), and a missing rate blocks.
 * supabase is always passed as a parameter — never imported directly.
 */

// ── Package tax ───────────────────────────────────────────────────────────────
//
// The model signed off on 2026-10-23, applied PER PACKAGE — never to a total.
// Packages are summed only after each one has been taxed:
//
//   R   = fob_paquete × (1 + flete_rate + seguro_rate)
//   IVA = R × iva_rate                                  (always)
//   DAI = R > threshold_usd ? (R + IVA) × dai_rate : 0
//
// R and IVA are rounded to cents before they feed the next step, so the line
// split matches what the client and the DGA see: FOB $194.42 → R $213.86,
// IVA $27.80, DAI $45.92. The boundary is on R: $199.99 no DAI, $200.01 DAI.

export const INVOICE_TAX_KEYS = ['threshold_usd', 'dai_rate', 'iva_rate', 'flete_rate', 'seguro_rate'];

// Stamped on every package's taxes in declaration_drafts. The invoice only
// trusts entries carrying this stamp — older ones were computed with the
// retired 1.1595 / 0.30 formula.
export const PACKAGE_TAX_FORMULA = 'paquete-2026-10-23';

// Round to cents. toPrecision(12) first strips binary noise (173.92499999… →
// 173.925) so half-cent values round up the way a calculator would.
const cents = (n) => Math.round(Number((n * 100).toPrecision(12))) / 100;

/** Keys of INVOICE_TAX_KEYS that the config row is missing or has non-numeric. */
export function missingInvoiceTaxKeys(config) {
  return INVOICE_TAX_KEYS.filter((k) => {
    const v = config?.[k];
    return v === null || v === undefined || v === '' || !Number.isFinite(Number(v));
  });
}

/** True when the config row carries all five rates calcPackageTax needs. */
export const customsConfigComplete = (config) => !!config && missingInvoiceTaxKeys(config).length === 0;

/**
 * FOB of ONE package: Σ quantity × unit_value over that package's items.
 * Shape matches shipment_items and encomienda_items rows. Never pass items
 * from more than one package.
 */
export function packageFob(items) {
  return cents((items || []).reduce(
    (sum, it) => sum + (parseFloat(it?.unit_value) || 0) * (parseInt(it?.quantity) || 1),
    0
  ));
}

/**
 * Customs tax for one package.
 *
 * @param {number|string|null} fob  the package's FOB; null/blank is 0
 * @param {object} config  customs_config row for the package's tipo
 * @returns {{ r: number, iva: number, dai: number, total: number }} cents
 * @throws when the config is missing or lacks a rate — never falls back
 */
export function calcPackageTax(fob, config) {
  const missing = config ? missingInvoiceTaxKeys(config) : INVOICE_TAX_KEYS;
  if (missing.length > 0) {
    throw new Error(`customs_config incompleto: falta ${missing.join(', ')}`);
  }
  const rate = (k) => Number(config[k]);
  const base = Number(fob ?? 0) || 0;
  const r   = cents(base * (1 + rate('flete_rate') + rate('seguro_rate')));
  const iva = cents(r * rate('iva_rate'));
  const dai = r > rate('threshold_usd') ? cents((r + iva) * rate('dai_rate')) : 0;
  return { r, iva, dai, total: cents(iva + dai) };
}

/**
 * Whether one package pays DAI under the config: true / false, or null when
 * the config is missing a rate (the caller shows no tier rather than guess).
 */
export function packageCrossesDai(items, config) {
  if (!customsConfigComplete(config)) return null;
  return calcPackageTax(packageFob(items), config).dai > 0;
}

// ── Config lookup ─────────────────────────────────────────────────────────────

export const CUSTOMS_CONFIG_TIPOS = ['casillero', 'encomienda'];

/**
 * The customs_config row for one tipo (customs_config.applies_to), as stored —
 * no defaults merged in, so a missing value is visible to the caller instead
 * of silently becoming a hardcoded rate. Casillero and encomienda have their
 * own rows, so there is deliberately no fallback to the other tipo's row.
 * Returns null when there is no row for the tipo, more than one, or it cannot
 * be read.
 *
 * @param {object} supabase
 * @param {'casillero'|'encomienda'} tipo
 */
export async function fetchCustomsConfigRow(supabase, tipo) {
  if (!CUSTOMS_CONFIG_TIPOS.includes(tipo)) {
    console.warn('[customsCalc] fetchCustomsConfigRow: unknown tipo', tipo);
    return null;
  }
  try {
    const { data, error } = await supabase.from('customs_config').select('*').eq('applies_to', tipo).maybeSingle();
    if (error) {
      console.warn('[customsCalc] fetchCustomsConfigRow error:', error.message);
      return null;
    }
    return data || null;
  } catch (err) {
    console.warn('[customsCalc] fetchCustomsConfigRow threw:', err?.message);
    return null;
  }
}
